import { useEffect, useRef, useState } from 'react'

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        opts: {
          sitekey: string
          callback: (token: string) => void
          'expired-callback'?: () => void
          'error-callback'?: () => void
          theme?: 'light' | 'dark' | 'auto'
        },
      ) => string
      reset: (widgetId?: string) => void
      remove: (widgetId?: string) => void
    }
    onTurnstileLoad?: () => void
  }
}

let scriptPromise: Promise<void> | null = null

function loadTurnstileScript(): Promise<void> {
  if (window.turnstile) return Promise.resolve()
  if (scriptPromise) return scriptPromise
  scriptPromise = new Promise((resolve, reject) => {
    window.onTurnstileLoad = () => resolve()
    const existing = document.querySelector('script[data-turnstile]')
    if (existing) {
      if (window.turnstile) resolve()
      return
    }
    const s = document.createElement('script')
    s.src = 'https://challenges.cloudflare.com/turnstile/v0/api.js?onload=onTurnstileLoad'
    s.async = true
    s.defer = true
    s.dataset.turnstile = '1'
    s.onerror = () => reject(new Error('Turnstile script failed to load'))
    document.head.appendChild(s)
  })
  return scriptPromise
}

export function TurnstileWidget({
  siteKey,
  onToken,
  onError,
}: {
  siteKey: string
  onToken: (token: string | null) => void
  onError?: (message: string) => void
}) {
  const ref = useRef<HTMLDivElement>(null)
  const widgetId = useRef<string | null>(null)
  const [ready, setReady] = useState(false)

  useEffect(() => {
    let cancelled = false
    void loadTurnstileScript()
      .then(() => {
        if (!cancelled) setReady(true)
      })
      .catch((e) => onError?.((e as Error).message))
    return () => {
      cancelled = true
    }
  }, [onError])

  useEffect(() => {
    if (!ready || !ref.current || !window.turnstile) return
    widgetId.current = window.turnstile.render(ref.current, {
      sitekey: siteKey,
      theme: 'auto',
      callback: (token) => onToken(token),
      'expired-callback': () => onToken(null),
      'error-callback': () => {
        onToken(null)
        onError?.('Security check failed — refresh and try again')
      },
    })
    return () => {
      if (widgetId.current && window.turnstile) {
        window.turnstile.remove(widgetId.current)
        widgetId.current = null
      }
    }
  }, [ready, siteKey, onToken, onError])

  return <div ref={ref} className="turnstile-wrap" aria-hidden="true" />
}
